/**
 * Wires `dashboard.model.ts` to the four reads behind the front page.
 *
 * Calls `fetchCollections` and then `fetchCollectionWorks` per collection for
 * the counts and the strip; `fetchMyActivity` plus a budgeted fan-out of
 * `fetchCollectionActivity` for the panel, merged and deduped in the model;
 * and `signWorkThumbnails` for only the works the strip will show. The first
 * pair is the screen and can fail it; the feeds and the photographs are
 * garnish and each swallow their own failure.
 *
 * The state the model cannot hold is the chosen collection — null until the
 * reader picks one, which is not "the first" because nothing has loaded yet —
 * and the raw feed entries, kept as they arrived so that "2 days ago" is read
 * from the clock at render rather than frozen at fetch time.
 */

import type { ActivityEntry, CollectionSummary } from "@ondo/shared";
import { useEffect, useState } from "react";

import { describeError } from "../collection/collection.model.ts";
import type { ChoiceGroup } from "../lib/choices.ts";
import {
  fetchCollectionActivity,
  fetchMyActivity,
  fetchCollectionWorks,
  fetchCollections,
} from "../lib/api.ts";
import { signWorkThumbnails } from "../lib/workImages.ts";
import {
  ACTIVITY_COLLECTION_BUDGET,
  ACTIVITY_ROW_LIMIT,
  COLLECTION_STRIP_LIMIT,
  DASHBOARD_COPY,
} from "./dashboard.constants.ts";
import {
  collectionChoices,
  describeEmpty,
  newestCollections,
  selectedCollection,
  toActivity,
  toStripCards,
  toTiles,
  worksIn,
} from "./dashboard.model.ts";
import type { ActivityRow, PlacedWork, StripCard, Tile } from "./dashboard.model.ts";

export type DashboardView =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | {
      kind: "ready";
      /** Counted across every collection. The panel below is the scoped half. */
      tiles: Tile[];
      /** The collection the strip is showing, by name. */
      collectionName: string;
      /** Empty when there is nothing to switch between. */
      collectionGroups: ChoiceGroup[];
      activeCollection: string;
      cards: StripCard[];
      /** Why the strip is empty, or null when it has cards. */
      emptyMessage: string | null;
      /** Empty when nothing has been recorded, or when the feed store is unconfigured. */
      activity: ActivityRow[];
    };

export function useDashboardController(
  workBase: string,
  /** The reader's own ONDO id, so their own acts read "by you". */
  viewerOndoId: string | null,
): {
  view: DashboardView;
  chooseCollection: (id: string) => void;
} {
  const [holdings, setHoldings] = useState<{
    collections: CollectionSummary[];
    placed: PlacedWork[];
  } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [images, setImages] = useState<Map<string, string>>(new Map());
  // Kept as they arrived, and turned into rows at render, so the "2 days ago"
  // on each one is read from the clock now rather than frozen at fetch time.
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  // Null until the reader picks one, which is not the same as "the first": the
  // collections have not loaded yet at this point, so there is no first to name.
  const [chosen, setChosen] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    fetchCollections()
      .then(async (collections) => {
        const batches = await Promise.all(
          collections.map(async (collection) => {
            const works = await fetchCollectionWorks(collection.id);
            // The pairing is made by the request that asked for it. A
            // WorkSummary carries no collection, so this is the only moment
            // the two are knowably together.
            return works.map((work) => ({ work, collectionId: collection.id }));
          }),
        );
        if (active) setHoldings({ collections, placed: batches.flat() });
      })
      .catch((error: unknown) => {
        if (active) setFailure(describeError(error));
      });

    return () => {
      active = false;
    };
  }, []);

  // Photographs and activity load after the counts, and neither can fail the
  // screen: a dashboard that shows nothing because one signed URL was refused
  // would be worse than one with a placeholder in a card.
  const showing = holdings ? selectedCollection(holdings.collections, chosen) : null;
  const shown = holdings ? worksIn(holdings.placed, showing?.id ?? null) : [];

  useEffect(() => {
    if (!holdings) return;
    let active = true;

    void loadActivity(holdings.collections).then((recorded) => {
      if (active) setEntries(recorded);
    });

    return () => {
      active = false;
    };
  }, [holdings]);

  // Signed separately from the activity above, and keyed on the collection on
  // screen: only the strip's own works are worth a signature, and switching
  // collections changes which those are.
  const showingId = showing?.id ?? null;
  useEffect(() => {
    if (!holdings || showingId === null) return;
    let active = true;

    void loadImages(worksIn(holdings.placed, showingId)).then((signed) => {
      if (active) setImages(signed);
    });

    return () => {
      active = false;
    };
  }, [holdings, showingId]);

  if (failure) {
    return { view: { kind: "error", message: failure }, chooseCollection: setChosen };
  }
  if (!holdings) return { view: { kind: "loading" }, chooseCollection: setChosen };

  const works = holdings.placed.map((placed) => placed.work);

  return {
    chooseCollection: setChosen,
    view: {
      kind: "ready",
      tiles: toTiles({ collections: holdings.collections, works }),
      collectionName: showing?.name ?? DASHBOARD_COPY.collections,
      // Offered whenever there are several. One collection is a heading, not a
      // control, and the panel head renders it as one.
      collectionGroups:
        holdings.collections.length > 1 ? collectionChoices(holdings.collections) : [],
      activeCollection: showing?.id ?? "",
      cards: toStripCards({
        placed: shown,
        workBase,
        images,
        limit: COLLECTION_STRIP_LIMIT,
      }),
      emptyMessage: describeEmpty({
        totalWorks: works.length,
        shownWorks: shown.length,
      }),
      activity: toActivity({
        entries,
        collections: holdings.collections,
        workBase,
        viewerOndoId,
        now: new Date(),
        limit: ACTIVITY_ROW_LIMIT,
      }),
    },
  };
}

/** Signs only the photographs the strip will actually show. */
async function loadImages(placed: PlacedWork[]): Promise<Map<string, string>> {
  return signWorkThumbnails(
    placed.slice(0, COLLECTION_STRIP_LIMIT).map(({ work }) => work),
  );
}

/**
 * The recorded history of the caller's collections, merged.
 *
 * ONE READ, of the caller's own trail.
 *
 * This fanned out across up to `ACTIVITY_COLLECTION_BUDGET` collections until
 * `GET /v1/activity?scope=me` existed, because the endpoint took one subject and
 * a person's history had no feed of its own. It has one now, and the personal
 * feed is strictly better than the fan-out was: it carries what this account
 * DID and what was DONE TO IT — a verification is written to both parties'
 * feeds — which no collection feed ever held.
 *
 * The collection fan-out stays as the fallback, and not out of caution. A
 * collector who curates somebody else's collection sees acts in it that are not
 * their own, and those belong on this panel. Both are read and merged;
 * `toActivity` dedupes by activity id, so a row reached twice appears once.
 */
async function loadActivity(collections: CollectionSummary[]): Promise<ActivityEntry[]> {
  const newest = newestCollections(collections, ACTIVITY_COLLECTION_BUDGET);

  const feeds = await Promise.all([
    // An empty personal feed is a real answer — a new account has done nothing —
    // so a refusal here costs its rows and nothing else, exactly as one
    // collection's does.
    fetchMyActivity().catch((): ActivityEntry[] => []),
    ...newest.map((collection) =>
      fetchCollectionActivity(collection.id).catch((): ActivityEntry[] => []),
    ),
  ]);

  return feeds.flat();
}
